import { AfterViewInit, Component, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { AngularEditorComponent, AngularEditorConfig, AngularEditorService } from '@kolkov/angular-editor';
import _ from 'lodash';
import { startWith } from 'rxjs';
import AppValues from 'src/app/common/app.values';
import FieldsConstants from 'src/app/common/fields.constants';
import { FileUploadDelegate } from 'src/app/forms/fields/components/files/file-upload.delegate';
import { FormFieldSate } from 'src/app/forms/fields/components/form-field.state';
import { OnFileUploaded } from 'src/app/interfaces/file.interface';
import { MaybeNull } from 'src/app/utils';
import { stripHtml } from 'string-strip-html';

@Component({
  selector: 'app-rich-editor-field',
  templateUrl: './rich-editor-field.component.html',
  styleUrls: ['./rich-editor-field.component.scss'],
  providers: [FileUploadDelegate, AngularEditorService],
})
export class RichEditorFieldComponent extends FormFieldSate implements OnInit, AfterViewInit {
  @ViewChild(AngularEditorComponent, { static: false }) public editor: AngularEditorComponent;
  @ViewChild('fileInput', { static: false }) public fileInput: ElementRef<HTMLInputElement>;

  public editorConfig: AngularEditorConfig = {
    editable: true,
    spellcheck: false,
    height: '300px',
    maxHeight: '300px',
    width: '100%',
    sanitize: false,
    toolbarHiddenButtons: [['insertVideo', 'fontName', 'insertImage']],
  };

  public imagesLeftCount = 0;
  public symbolsLeftCount = 0;
  public isLoading = false;

  public readonly MAX_IMAGES: number = FieldsConstants.MAX_RICH_TEXT_EDITOR_IMAGES_COUNT;
  public readonly MAX_LENGTH: number = FieldsConstants.MAX_RICH_TEXT_EDITOR_SYMBOLS_COUNT;

  private fileUploadDelegate = inject(FileUploadDelegate);
  private editorService = inject(AngularEditorService);

  public override ngOnInit(): void {
    super.ngOnInit();
    this.initializeCounters();
    this.subscribeToFileUploaded();
    this.subscribeToFileUploadedErrors();
    this.control.setValidators([this.richTextValidator()]);
  }

  public ngAfterViewInit(): void {
    (this.editor.textArea.nativeElement as HTMLInputElement).addEventListener(
      'paste',
      this.handleClipboardEvent.bind(this),
    );
  }

  public override ngOnDestroy(): void {
    super.ngOnDestroy();

    (this.editor.textArea.nativeElement as HTMLInputElement).removeEventListener(
      'paste',
      this.handleClipboardEvent.bind(this),
    );
  }

  public onFocus(): void {
    if (!this.editorService.savedSelection) {
      this.editorService.saveSelection();
    }
  }

  public hasError(error: string): boolean {
    return this.isFieldInvalid() && this.control.hasError(error);
  }

  public onFileChanged(event: Event & { target: HTMLInputElement }): void {
    if (!event.target.files?.length) {
      return;
    }

    this.isLoading = true;
    this.fileUploadDelegate.uploadFile({ event, attr: this.fieldAttribute, enableCrop: false });
    this.fileInput.nativeElement.value = '';
  }

  private subscribeToFileUploaded(): void {
    this.subscriptions.push(
      this.fileUploadDelegate.onFileChanged.subscribe((response: OnFileUploaded) => {
        this.insertImg(response);
        this.isLoading = false;
      }),
    );
  }

  private subscribeToFileUploadedErrors(): void {
    this.subscriptions.push(this.fileUploadDelegate.onErrorOccurred.subscribe(() => (this.isLoading = false)));
  }

  private handleClipboardEvent($event: ClipboardEvent): void {
    const clipboardData: MaybeNull<DataTransfer> = $event.clipboardData;
    const allowedType = 'Files';
    const changesEvent: Event = new Event('change');

    if (clipboardData && clipboardData.types.includes(allowedType)) {
      $event.preventDefault();

      this.fileInput.nativeElement.files = clipboardData.files;
      this.fileInput.nativeElement.dispatchEvent(changesEvent);
    }
  }

  private insertImg(img: OnFileUploaded): void {
    const textArea: HTMLElement = this.editor.textArea.nativeElement as HTMLInputElement;
    this.editor.focus();

    if (!this.editor.touched) {
      this.editorService.saveSelection();

      if (this.editorService.savedSelection) {
        this.editorService.savedSelection.selectNodeContents(textArea);

        this.editorService.savedSelection.collapse(false);
      }
    }

    this.editorService.insertHtml(this.createStringifiedHTMLImg(img));
  }

  private initializeCounters(): void {
    this.subscriptions.push(
      this.control.valueChanges.pipe(startWith(this.control.value)).subscribe((value: string) => {
        this.symbolsLeftCount = this.MAX_LENGTH - this.countSymbols(value);
        this.imagesLeftCount = this.MAX_IMAGES - this.countImages(value);
      }),
    );
  }

  private createStringifiedHTMLImg(img: OnFileUploaded): string {
    return `<img src="${img.link}" alt="${img.fileName ?? ''}">`;
  }

  private richTextValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const errors: ValidationErrors = {
        fieldIsEmpty: this.isFieldEmpty(control.value as string),
        maxLength: this.countSymbols(control.value as string) > this.MAX_LENGTH,
        maxImages: this.countImages(control.value as string) > this.MAX_IMAGES,
      };

      return Object.keys(_.pickBy(errors)).length ? _.pickBy(errors) : null;
    };
  }

  private countSymbols(richText = ''): number {
    const temporalDivElement: HTMLDivElement = document.createElement('div');
    temporalDivElement.innerHTML = richText;
    return temporalDivElement.textContent?.length ?? 0;
  }

  private isFieldEmpty(value: string): boolean {
    return stripHtml(value).result.length === 0;
  }

  private countImages(richText = ''): number {
    return (richText.match(AppValues.excludeImgTagsPattern) || []).length;
  }
}
